/**
 * CalendarForecastChart — line chart Recharts con due modalità:
 *   - 'month'    (default): plot della finestra calendario visibile
 *   - 'rolling60': self-fetch today-30 → today+30 e plot di quella finestra
 *
 * Il toggle "Rolling 60g" risponde all'idea backlog originale ("Forecast 30
 * giorni") dando una vista temporale unificata che attraversa il confine del
 * mese, utile per cogliere trend a cavallo (es. coda di marzo + slancio
 * aprile). La modalità monthly resta default perché coerente con la calendar
 * grid sotto.
 *
 * Estratto da `CalendarTab.js` il 2026-05-29 per ridurre la cc del file
 * principale. API/data-testid invariati.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis,
  CartesianGrid, Tooltip as RTooltip, Legend, ReferenceLine,
} from 'recharts';
import { Info } from 'lucide-react';
import api from '../lib/api';
import { fmt } from '../lib/calendarFormat';


/**
 * Hook che gestisce il fetch della finestra rolling60. Restituisce
 * `{ data, loading }` dove `data` ha shape `{ rows, forecast, dailyBudgetTargets, dates }`
 * o `null` finché non viene attivata la modalità rolling.
 */
function useRollingWindow(mode, storeId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mode !== 'rolling60' || !storeId) return;
    const today = new Date();
    const fmtDate = (d) => d.toISOString().slice(0, 10);
    const from = new Date(today); from.setDate(today.getDate() - 30);
    const to = new Date(today);   to.setDate(today.getDate() + 30);
    setLoading(true);
    api.get(`/stores/${storeId}/kpi-daily`, { params: { from_: fmtDate(from), to: fmtDate(to) } })
      .then(res => {
        const dates = [];
        const cur = new Date(from);
        while (cur <= to) {
          dates.push(fmtDate(cur));
          cur.setDate(cur.getDate() + 1);
        }
        setData({
          rows: res.data.rows || [],
          forecast: res.data.forecast || { forecasts: {}, today: null },
          dailyBudgetTargets: res.data.daily_budget_targets || {},
          dates,
        });
      })
      .catch(() => setData({ rows: [], forecast: { forecasts: {}, today: null }, dailyBudgetTargets: {}, dates: [] }))
      .finally(() => setLoading(false));
  }, [mode, storeId]);

  return { data, loading };
}


/**
 * Trasforma sorgenti grezze (`rows`, `forecast`, `dailyBudgetTargets`,
 * `dates`) nella struttura piatta consumabile da Recharts:
 *   `[{ day, label, iso, Attuale, Forecast, Budget }, ...]`
 *
 * Budget priority: scenario=budget row → daily_budget_targets fallback.
 * `seed` collega la linea Attuale al primo punto Forecast il giorno di
 * `forecast.today` per non avere un gap visivo brutto.
 */
function buildChartData({ sourceRows, sourceForecast, sourceBudget, sourceDates, isRolling }) {
  if (!sourceDates || sourceDates.length === 0) return [];
  const actualByDate = {};
  const budgetFromRows = {};
  for (const r of sourceRows || []) {
    if (r.scenario === 'actual' && r.kpi_key === 'total_sales' && r.value != null) {
      actualByDate[r.date] = r.value;
    } else if (r.scenario === 'budget' && r.kpi_key === 'total_sales' && r.value != null) {
      budgetFromRows[r.date] = r.value;
    }
  }
  const forecasts = sourceForecast?.forecasts || {};
  const today = sourceForecast?.today;
  let seedSet = false;
  return sourceDates.map(iso => {
    const day = parseInt(iso.slice(8, 10), 10);
    const label = isRolling ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}` : day;
    const actual = actualByDate[iso] ?? null;
    let forecastVal = forecasts[iso] ?? null;
    if (!seedSet && actual != null && today && iso === today) {
      forecastVal = forecastVal ?? actual;
      seedSet = true;
    }
    const dailyBudgetObj = sourceBudget?.[iso];
    const budgetVal = budgetFromRows[iso]
      ?? (dailyBudgetObj && typeof dailyBudgetObj === 'object'
          ? (dailyBudgetObj.total_sales ?? null)
          : (typeof dailyBudgetObj === 'number' ? dailyBudgetObj : null));
    return {
      day, label, iso,
      Attuale: actual,
      Forecast: forecastVal,
      Budget: budgetVal,
    };
  });
}


function ChartModeToggle({ mode, onChange }) {
  return (
    <div className="inline-flex border border-slate-300 text-[11px]" data-testid="forecast-chart-mode">
      <button
        type="button"
        onClick={() => onChange('month')}
        className={`px-2.5 py-1 transition-colors ${
          mode === 'month' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
        }`}
        data-testid="forecast-chart-mode-month"
      >
        Mese
      </button>
      <button
        type="button"
        onClick={() => onChange('rolling60')}
        className={`px-2.5 py-1 transition-colors ${
          mode === 'rolling60' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
        }`}
        data-testid="forecast-chart-mode-rolling60"
      >
        Rolling 60g
      </button>
    </div>
  );
}


export function CalendarForecastChart({ storeId, rows, forecast, dailyBudgetTargets, gridDates }) {
  const [mode, setMode] = useState('month');
  const { data: rollingData, loading: rollingLoading } = useRollingWindow(mode, storeId);

  // Sceglie la sorgente dati in base al mode. Per il rolling, gli "X-axis label"
  // sono date complete (MM-dd) invece del solo numero del giorno, perché
  // attraversiamo i mesi.
  const isRolling = mode === 'rolling60';
  const sourceRows = useMemo(
    () => (isRolling ? (rollingData?.rows || []) : rows),
    [isRolling, rollingData, rows]
  );
  const sourceForecast = useMemo(
    () => (isRolling ? (rollingData?.forecast || { forecasts: {}, today: null }) : forecast),
    [isRolling, rollingData, forecast]
  );
  const sourceBudget = useMemo(
    () => (isRolling ? (rollingData?.dailyBudgetTargets || {}) : dailyBudgetTargets),
    [isRolling, rollingData, dailyBudgetTargets]
  );
  const sourceDates = useMemo(
    () => (isRolling ? (rollingData?.dates || []) : gridDates),
    [isRolling, rollingData, gridDates]
  );

  const data = useMemo(
    () => buildChartData({ sourceRows, sourceForecast, sourceBudget, sourceDates, isRolling }),
    [sourceRows, sourceForecast, sourceBudget, sourceDates, isRolling],
  );

  const hasActual = data.some(d => d.Attuale != null);
  const hasForecast = data.some(d => d.Forecast != null);
  if (!hasActual && !hasForecast && !rollingLoading) return null;

  const today = sourceForecast?.today;
  // In monthly mode la ReferenceLine usa il numero del giorno; in rolling mode
  // usa l'etichetta MM/dd. ReferenceLine confronta esattamente il valore del
  // `dataKey` X — qui dataKey="label".
  const todayInWindow = today && data.some(d => d.iso === today);
  const todayX = todayInWindow ? data.find(d => d.iso === today).label : null;

  return (
    <div className="bg-white border border-slate-200" data-testid="forecast-line-chart">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
        <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-700">
          Andamento giornaliero · Attuale + Forecast 30g
        </h2>
        <div className="flex items-center gap-3">
          <ChartModeToggle mode={mode} onChange={setMode} />
          <Info
            className="w-3.5 h-3.5 text-slate-400 cursor-help flex-shrink-0"
            aria-label="Come è calcolato il forecast"
            title={
              "Linea solida nera: vendite giornaliere già importate (actuals). "
              + "Linea tratteggiata grigia: proiezione sui giorni futuri (media stesso "
              + "giorno-settimana ultime 8 sett. × stagionalità mensile). "
              + "Linea viola tratteggiata: target giornaliero da budget (se configurato). "
              + "Toggle Mese/Rolling 60g: scegli se vedere il mese visibile o ±30 giorni da oggi."
            }
          />
        </div>
      </div>
      <div className="p-3" style={{ height: 260 }}>
        {rollingLoading ? (
          <div className="h-full flex items-center justify-center text-xs text-slate-400">
            Caricamento finestra rolling…
          </div>
        ) : (
        <ResponsiveContainer width="100%" height="100%">
          {/* margin.top aumentato (8 → 28) per non clippare il label "oggi"
              della ReferenceLine. ComposedChart per supportare Area
              gradient sotto la linea Forecast (restyle 2026-05-14). */}
          <ComposedChart data={data} margin={{ top: 28, right: 16, left: 0, bottom: 0 }}>
            <defs>
              {/* Gradient verticale soft per l'area forecast: dal grigio
                  visibile in basso a trasparente in alto, così la silhouette
                  della proiezione si percepisce senza disturbare la lettura
                  della linea actuals. */}
              <linearGradient id="forecastGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#64748b" stopOpacity={0.02} />
                <stop offset="100%" stopColor="#64748b" stopOpacity={0.22} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#64748b' }}
              stroke="#cbd5e1"
              interval={isRolling ? 6 : 2}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#64748b' }}
              stroke="#cbd5e1"
              tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
            />
            <RTooltip
              contentStyle={{
                fontSize: 11, border: '1px solid #cbd5e1',
                background: '#fff', borderRadius: 0,
              }}
              labelFormatter={(label, payload) => {
                const p = payload?.[0]?.payload;
                return p?.iso || `${label}`;
              }}
              formatter={(v) => v == null ? '-' : fmt(v, 'currency')}
            />
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
            {todayX != null && (
              <ReferenceLine
                x={todayX}
                stroke="#475569"
                strokeWidth={1.5}
                strokeDasharray="3 3"
                label={{
                  value: 'oggi',
                  position: 'insideTopRight',
                  offset: 8,
                  fontSize: 10,
                  fontWeight: 600,
                  fill: '#475569',
                }}
              />
            )}
            {/* Area forecast — render PRIMA delle linee per stare sotto.
                Restyle 2026-05-14 (idea_0bbfb95e4434): l'utente trovava
                la linea tratteggiata grigia poco leggibile; l'area con
                gradient soft rende immediatamente percepibile l'inviluppo
                della proiezione. */}
            {hasForecast && (
              <Area
                type="monotone"
                dataKey="Forecast"
                stroke="none"
                fill="url(#forecastGradient)"
                fillOpacity={1}
                connectNulls={false}
                isAnimationActive={false}
                legendType="none"
              />
            )}
            <Line
              type="monotone"
              dataKey="Attuale"
              stroke="#0f172a"
              strokeWidth={2.5}
              dot={{ r: 2.5 }}
              connectNulls={false}
              isAnimationActive={false}
            />
            {hasForecast && (
              <Line
                type="monotone"
                dataKey="Forecast"
                stroke="#64748b"
                strokeWidth={1.75}
                dot={false}
                activeDot={{ r: 4, fill: '#64748b' }}
                connectNulls={false}
                isAnimationActive={false}
              />
            )}
            <Line
              type="monotone"
              dataKey="Budget"
              // Viola saturo, spessore 2.5 e dash 8/3 per emergere
              // distintivamente dalla griglia. Sempre renderizzata
              // quando ci sono target daily (no conditional render)
              // — fix 2026-05-14 (idea_0bbfb95e4434).
              stroke="#7c3aed"
              strokeWidth={2.5}
              strokeDasharray="8 3"
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
